'use client'

import { useMedia } from 'react-use'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { cn } from '@/lib/utils'

interface IResponsiveModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string | React.ReactNode
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  className?: string
  contentClassName?: string
  isModal?: boolean
}

/**
 * A responsive modal component that displays as a modal on desktop and a drawer on mobile
 * @param open - Whether the modal is open
 * @param onOpenChange - Callback when the modal open state changes
 * @param title - The title of the modal
 * @param description - Optional description text
 * @param children - The main content of the modal
 * @param footer - Optional footer content (buttons, actions, etc.)
 * @param className - Optional className for the root container
 * @param contentClassName - Optional className for the content area
 * @param isModal - If true, always use Dialog (no drawer on mobile). Use when nested inside another modal.
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  contentClassName,
  isModal = false,
}: IResponsiveModalProps) {
  const isMobile = useMedia('(max-width: 768px)', false)

  // When used inside another modal, always use Dialog to avoid nested drawer issues
  const shouldUseDrawer = isMobile && !isModal

  if (shouldUseDrawer) {
    return (
      <Drawer
        data-id="responsive-modal-drawer"
        open={open}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className={cn('', className)}>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            {description && (
              <DrawerDescription>{description}</DrawerDescription>
            )}
          </DrawerHeader>
          <div className={cn('px-4', contentClassName)}>{children}</div>
          {footer && <DrawerFooter>{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      data-id="responsive-modal-dialog"
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className={cn('', className)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className={cn('', contentClassName)}>{children}</div>
        {footer}
      </DialogContent>
    </Dialog>
  )
}
